import * as React from "react";
import { Badge, Text } from "@salestrak/ui";

import type { VariableLabelResolver } from "./automation-variables";
import { at } from "#app/lib/utils/at";

// Matches the backend's reference regex: `{{ path }}` with optional whitespace.
const VARIABLE_RE = /\{\{\s*([^}]+?)\s*\}\}/g;

// The soft-brand chip treatment, shared verbatim with VariableTextInput's
// in-editor chips (variable-token.tsx) so a variable reads identically whether
// it's being edited or recapped. Badge ships no brand variant — the
// brand-subtle wash / brand text pair are library tokens, applied here as the
// tracked className gap rather than a forked wrapper.
export const VARIABLE_CHIP_CLASS =
  "rounded-full bg-primary-subtle text-primary-subtle-foreground";

// A single read-only variable chip.
export function VariableChip({ label }: { label: string }) {
  return (
    <Badge className={VARIABLE_CHIP_CLASS} size="sm" variant="secondary">
      {/* No truncate: line-clamp's -webkit-box display breaks vertical
          centering inside the badge's flex row, and the badge already clips
          overflow (whitespace-nowrap + overflow-hidden). Matches the editor
          chip. */}
      <Text size="2xs">{label}</Text>
    </Badge>
  );
}

// Renders a stored template string as an inline run of literal Text and
// `{{ path }}` references resolved to friendly Badge chips — the read-only
// counterpart to VariableTextInput's editor chips. Only KNOWN references chip
// (labelFor returns a label); anything else stays literal text, matching the
// editor's conversion rules. Place inside a row Box so the pieces flow
// horizontally. Literal runs render muted to match caption contexts (the node
// recap), letting the chips carry the emphasis.
export function VariableChips({
  text,
  labelFor,
}: {
  text: string;
  labelFor: VariableLabelResolver;
}) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  VARIABLE_RE.lastIndex = 0;
  while ((match = VARIABLE_RE.exec(text))) {
    const label = labelFor(at(match, 1, "variable path").trim());
    if (label === null) continue;
    if (match.index > last) {
      parts.push(
        <Text key={key++} size="xs" truncate={1} variant="muted">
          {text.slice(last, match.index)}
        </Text>,
      );
    }
    parts.push(<VariableChip key={key++} label={label} />);
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parts.push(
      <Text key={key++} size="xs" truncate={1} variant="muted">
        {text.slice(last)}
      </Text>,
    );
  }
  return <>{parts}</>;
}
